import { useMemo, useState } from "react";
import { MoreHorizontal } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useCompanyMembers, type CompanyMemberRow } from "@/hooks/useCompanyMembers";
import type { CompanyRow } from "@/hooks/useCompanyFilters";

interface Props {
  company: CompanyRow | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type PendingAction = "suspend" | "reactivate" | "deactivate" | "grant_admin" | "remove_admin";

interface PendingState {
  userId: string;
  action: PendingAction;
  reason: string;
  submitting: boolean;
}

const extractError = (e: unknown): string => {
  if (e && typeof e === "object") {
    if ("message" in e && typeof (e as any).message === "string") {
      return (e as any).message;
    }
  }
  return "Action failed";
};

const RoleBadge = ({ role }: { role: string | null }) => {
  if (role === "admin") {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-500/15 text-blue-400 border border-blue-500/30">
        Admin
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-700 text-slate-300 border border-slate-600">
      Member
    </span>
  );
};

const StatusBadge = ({ status }: { status: string | null }) => {
  if (status === "active") {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">
        Active
      </span>
    );
  }
  if (status === "suspended") {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-500/15 text-amber-400 border border-amber-500/30">
        Suspended
      </span>
    );
  }
  if (status === "deactivated") {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-700 text-slate-300 border border-slate-600">
        Deactivated
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-700 text-slate-300 border border-slate-600">
      {status ?? "Unknown"}
    </span>
  );
};

const SkeletonRow = () => (
  <div className="flex items-center justify-between gap-3 p-3 border border-slate-800 rounded">
    <div className="space-y-2">
      <div className="h-4 w-32 rounded bg-slate-700 animate-pulse" />
      <div className="h-3 w-48 rounded bg-slate-700 animate-pulse" />
    </div>
    <div className="h-5 w-16 rounded bg-slate-700 animate-pulse" />
  </div>
);

export default function CompanyMembersSheet({ company, open, onOpenChange }: Props) {
  const companyId = company?.id ?? null;
  const { members, isLoading, error, refetch } = useCompanyMembers(companyId);
  const queryClient = useQueryClient();

  const [showAll, setShowAll] = useState(false);
  const [pending, setPending] = useState<PendingState | null>(null);

  const filtered = useMemo<CompanyMemberRow[]>(() => {
    if (showAll) return members;
    return members.filter((m) => m.profile_status === "active");
  }, [members, showAll]);

  const startAction = (userId: string, action: PendingAction) => {
    setPending({ userId, action, reason: "", submitting: false });
  };

  const cancelAction = () => {
    if (pending?.submitting) return;
    setPending(null);
  };

  const invalidate = () => {
    if (!companyId) return;
    queryClient.invalidateQueries({ queryKey: ["company-members", companyId] });
    queryClient.invalidateQueries({ queryKey: ["companies"] });
  };

  const submitAction = async () => {
    if (!pending || !company) return;
    const { userId, action, reason } = pending;
    if ((action === "suspend" || action === "deactivate") && !reason.trim()) return;

    setPending({ ...pending, submitting: true });

    try {
      if (action === "suspend") {
        const { error: rpcErr } = await supabase.rpc("suspend_member", {
          p_user_id: userId,
          p_company_id: company.id,
          p_reason: reason.trim(),
          p_suspended_until: null,
        });
        if (rpcErr) throw rpcErr;
        toast.success("Member suspended");
      } else if (action === "reactivate") {
        const { error: rpcErr } = await supabase.rpc("reactivate_company_member", {
          p_user_id: userId,
          p_company_id: company.id,
        });
        if (rpcErr) throw rpcErr;
        toast.success("Member reactivated");
      } else if (action === "deactivate") {
        const { error: rpcErr } = await supabase.rpc("deactivate_user_from_company", {
          p_user_id: userId,
          p_company_id: company.id,
          p_reason: reason.trim(),
        });
        if (rpcErr) throw rpcErr;
        toast.success("Member deactivated");
      } else if (action === "grant_admin") {
        const { error: rpcErr } = await supabase.rpc("grant_admin_role", {
          p_user_id: userId,
          p_company_id: company.id,
        });
        if (rpcErr) throw rpcErr;
        toast.success("Admin role granted");
      } else if (action === "remove_admin") {
        const { error: rpcErr } = await supabase.rpc("remove_admin_role", {
          p_user_id: userId,
          p_company_id: company.id,
        });
        if (rpcErr) throw rpcErr;
        toast.success("Admin role removed");
      }
      invalidate();
      setPending(null);
    } catch (e) {
      const raw = extractError(e);
      const msg = raw.toLowerCase().includes("last") || raw.toLowerCase().includes("admin")
        ? "Cannot remove the last admin. Promote another member to admin first, then remove this role."
        : raw;
      toast.error(msg);
      setPending((prev) => (prev ? { ...prev, submitting: false } : null));
    }
  };

  const actionLabel = (a: PendingAction): string =>
    a === "suspend" ? "Suspend Member"
    : a === "reactivate" ? "Reactivate Member"
    : a === "deactivate" ? "Deactivate Member"
    : a === "grant_admin" ? "Grant Admin Role"
    : "Remove Admin Role";

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="bg-slate-900 border-slate-700 text-slate-100 sm:max-w-md w-full overflow-y-auto"
      >
        <SheetHeader>
          <SheetTitle className="text-white">
            {company?.name ?? "Company"} — Members
          </SheetTitle>
          <SheetDescription className="text-slate-400">
            Showing {filtered.length} of {members.length} members
          </SheetDescription>
        </SheetHeader>

        <div className="mt-4 flex items-center justify-between">
          <Label htmlFor="show-all-members" className="text-sm text-slate-300">
            Show all members
          </Label>
          <Switch
            id="show-all-members"
            checked={showAll}
            onCheckedChange={setShowAll}
          />
        </div>

        <div className="mt-4 space-y-2">
          {error ? (
            <div className="flex flex-col items-center gap-3 p-6 text-center">
              <p className="text-sm text-red-400">{error}</p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => refetch()}
                className="bg-slate-800 border-slate-700 text-slate-200 hover:bg-slate-700"
              >
                Retry
              </Button>
            </div>
          ) : isLoading ? (
            Array.from({ length: 4 }).map((_, i) => <SkeletonRow key={i} />)
          ) : filtered.length === 0 ? (
            <div className="p-6 text-center text-sm text-slate-500">
              No members found.
            </div>
          ) : (
            filtered.map((m) => {
              const fullName = [m.first_name, m.last_name].filter(Boolean).join(" ").trim() || "—";
              const isPending = pending?.userId === m.user_id;
              const profileStatus = m.profile_status;
              const showSuspend = profileStatus === "active";
              const showReactivate = profileStatus === "suspended";
              const showDeactivate = profileStatus === "active" || profileStatus === "suspended";
              const hasActions = showSuspend || showReactivate || showDeactivate;

              return (
                <div
                  key={m.user_id}
                  className="border border-slate-800 rounded bg-slate-900"
                >
                  <div className="flex items-start justify-between gap-3 p-3">
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-white truncate">{fullName}</div>
                      <div className="text-xs text-slate-400 truncate">{m.email ?? "—"}</div>
                      <div className="mt-2 flex flex-wrap items-center gap-1.5">
                        <RoleBadge role={m.member_role} />
                        <StatusBadge status={profileStatus} />
                        {m.suspended_by_company && profileStatus === "suspended" && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-500/15 text-amber-400 border border-amber-500/30">
                            Via company
                          </span>
                        )}
                      </div>
                    </div>
                    {hasActions && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            type="button"
                            className="inline-flex items-center justify-center h-7 w-7 rounded text-slate-400 hover:bg-slate-700 hover:text-white shrink-0"
                            aria-label="Member actions"
                          >
                            <MoreHorizontal className="h-4 w-4" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                          align="end"
                          className="bg-slate-800 border-slate-700 text-slate-100"
                        >
                          {m.member_role !== "admin" && m.profile_status === "active" && (
                            <DropdownMenuItem
                              onClick={() => startAction(m.user_id, "grant_admin")}
                              className="text-blue-400 focus:bg-slate-700 focus:text-blue-300"
                            >
                              Make Admin
                            </DropdownMenuItem>
                          )}
                          {m.member_role === "admin" && (
                            <DropdownMenuItem
                              onClick={() => startAction(m.user_id, "remove_admin")}
                              className="text-slate-400 focus:bg-slate-700 focus:text-slate-300"
                            >
                              Remove Admin
                            </DropdownMenuItem>
                          )}
                          {showSuspend && (
                            <DropdownMenuItem
                              onClick={() => startAction(m.user_id, "suspend")}
                              className="text-amber-400 focus:bg-slate-700 focus:text-amber-300"
                            >
                              Suspend
                            </DropdownMenuItem>
                          )}
                          {showReactivate && (
                            <DropdownMenuItem
                              onClick={() => startAction(m.user_id, "reactivate")}
                              className="text-emerald-400 focus:bg-slate-700 focus:text-emerald-300"
                            >
                              Reactivate
                            </DropdownMenuItem>
                          )}
                          {showDeactivate && (
                            <DropdownMenuItem
                              onClick={() => startAction(m.user_id, "deactivate")}
                              className="text-red-400 focus:bg-slate-700 focus:text-red-300"
                            >
                              Deactivate
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>

                  {isPending && pending && (
                    <div className="border-t border-slate-800 p-3 space-y-2 bg-slate-800/40">
                      <div className="text-xs font-medium text-slate-200">
                        {actionLabel(pending.action)}
                      </div>
                      {(pending.action === "suspend" || pending.action === "deactivate") && (
                        <Input
                          value={pending.reason}
                          onChange={(e) =>
                            setPending((prev) => (prev ? { ...prev, reason: e.target.value } : null))
                          }
                          placeholder="Reason (required)"
                          className="bg-slate-900 border-slate-700 text-slate-100 placeholder:text-slate-500 h-8 text-sm"
                          autoFocus
                          disabled={pending.submitting}
                        />
                      )}
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={cancelAction}
                          disabled={pending.submitting}
                          className="bg-slate-800 border-slate-700 text-slate-200 hover:bg-slate-700 h-7 text-xs"
                        >
                          Cancel
                        </Button>
                        <Button
                          size="sm"
                          onClick={submitAction}
                          disabled={
                            pending.submitting ||
                            ((pending.action === "suspend" || pending.action === "deactivate") &&
                              !pending.reason.trim())
                          }
                          className="h-7 text-xs"
                        >
                          {pending.submitting ? "Working…" : "Confirm"}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
